import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'

import { toastFailed } from 'utils/toast'
import { downloadFileUrl } from 'utils/downloadFileUrl'
import { getHubs } from 'utils/api'
import {
  getBudgetBulkUploadTemplate,
  getPlanningBulkUploadTemplate,
  getWorkingScheduleBulkUploadTemplate,
  postUploadManPowerPlanning,
  postUploadManPowerBudget,
  postUploadWorkingSchedule,
  getDepartementList,
} from 'features/Enterprise/HRIS/services/mpBulkUpload'

import {
  fetchHiringFunnelPrioritizationTemplate,
  postHiringFunnelPrioritizationBulkUpload,
  fetchHiringFunnelJobRequestTemplate,
  postHiringFunnelJobRequestBulkUpload,
} from 'features/Enterprise/Account/services/mpHiringFunnel'

type StatusType = 'FAILED' | 'SUCCESS' | 'PARTIAL_SUCCESS'

interface ManPowerBulkUploadInterface {
  isLoadingDownloadTemplate: boolean
  isShowDownloadTemplateDialog: boolean
  dateRangeTemplate: { startDate: string; endDate: string } | null
  hubsList: Array<{ id: string; name: string }>
  selectedHub: { location_id: number; location_name: string } | null
  statusUpload: string
  resultUpload: Record<string, unknown>
  selectedMonth: string | null
  selectedDepartmentString: string
  allDepartment: Array<{ content: string[]; name: string }>
}

const initialState: ManPowerBulkUploadInterface = {
  isLoadingDownloadTemplate: false,
  isShowDownloadTemplateDialog: false,
  dateRangeTemplate: null,
  hubsList: [],
  selectedHub: null,
  statusUpload: 'idle',
  resultUpload: {},
  selectedMonth: null,
  selectedDepartmentString: ``,
  allDepartment: [],
}

const ManPowerBulkUpload = createSlice({
  name: 'manPowerBulkUpload',
  initialState,
  reducers: {
    reset: () => initialState,
    openDownloadTemplateDialog: (state, action) => {
      const isShow = action.payload
      state.isShowDownloadTemplateDialog = isShow
    },
    setDateRange: (state, action) => {
      const dateRange = action.payload
      state.dateRangeTemplate = dateRange
    },
    setHubList: (state, action) => {
      state.hubsList = action.payload
    },
    setSelectedHub: (state, action) => {
      state.selectedHub = action.payload
    },
    setSelectedMonth: (state, action) => {
      state.selectedMonth = action.payload
    },
    setSelectedDepartmentString: (state, action) => {
      state.selectedDepartmentString = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(downloadTemplate.pending, (state) => {
        state.isLoadingDownloadTemplate = true
      })
      .addCase(downloadTemplate.fulfilled, (state) => {
        state.isLoadingDownloadTemplate = false
        state.isShowDownloadTemplateDialog = false
      })
      .addCase(downloadTemplate.rejected, (state) => {
        state.isLoadingDownloadTemplate = false
      })
      .addCase(onUploadFile.pending, (state) => {
        state.statusUpload = 'uploadOnProgress'
      })
      .addCase(onUploadFile.fulfilled, (state, action) => {
        const { status, ...rest } = action.payload
        const statusMap: Record<StatusType, string> = {
          FAILED: 'uploadFailed',
          SUCCESS: 'uploadSucceded',
          PARTIAL_SUCCESS: 'partialSuccess',
        }
        state.statusUpload = statusMap[status as StatusType] || 'idle'
        state.resultUpload = rest
      })
      .addCase(getAllDepartment.fulfilled, (state, action) => {
        state.allDepartment = action.payload
      })
  },
})

export const {
  openDownloadTemplateDialog,
  setDateRange,
  reset,
  setHubList,
  setSelectedHub,
  setSelectedMonth,
  setSelectedDepartmentString,
} = ManPowerBulkUpload.actions

export const getAllHubs = createAsyncThunk(
  'manPowerBulkUpload/getAllHubs',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getHubs()
      dispatch(setHubList(data))
      return true
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const downloadTemplate = createAsyncThunk(
  'manPowerBulkUpload/downloadTemplate',
  async (
    type: 'workingSchedule' | 'hiringFunnel' | 'planning' | 'budget' | 'jobRequest',
    { rejectWithValue, getState },
  ) => {
    try {
      const { selectedHub, dateRangeTemplate, selectedDepartmentString } = (
        getState() as StoreStateType
      ).manPowerBulkUpload

      const { startDate = '', endDate = '' } = dateRangeTemplate || {}
      const payloadParams = {
        start_date: startDate || '',
        end_date: endDate || '',
      }
      const response = await {
        budget: () => getBudgetBulkUploadTemplate(payloadParams),
        planning: () =>
          getPlanningBulkUploadTemplate({
            ...payloadParams,
            location: selectedHub?.location_id || 0,
          }),
        workingSchedule: () =>
          getWorkingScheduleBulkUploadTemplate({
            start_date: startDate.split('-').join(''),
            end_date: endDate.split('-').join(''),
            location_id: selectedHub?.location_id || 0,
            department: selectedDepartmentString,
          }),
        hiringFunnel: () => fetchHiringFunnelPrioritizationTemplate(),
        jobRequest: () => fetchHiringFunnelJobRequestTemplate(),
      }[type]()
      const {
        data: { file_url },
      } = response.data
      downloadFileUrl(file_url)
      return file_url
    } catch (err) {
      let errMessage = ''
      if (err instanceof AxiosError) {
        errMessage = err.response?.data?.error?.message || err.message
      }
      return rejectWithValue(errMessage)
    }
  },
)

export const getAllDepartment = createAsyncThunk(
  'manPowerBulkUpload/getAllDepartment',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDepartementList()
      const dataObject = response?.data?.data
      const keys = Object.keys(response?.data?.data)
      const dataProcessed: { content: string[]; name: string }[] = []

      keys.forEach((key) => {
        dataProcessed.push({ content: dataObject[key].names, name: key })
      })

      return dataProcessed
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const onDownloadTempalte = createAsyncThunk(
  'manPowerBulkUpload/onDownloadTempalte',
  async (
    type: 'workingSchedule' | 'hiringFunnel' | 'planning' | 'budget' | 'jobRequest',
    { getState, dispatch },
  ) => {
    const {
      manPowerBulkUpload: { selectedHub, dateRangeTemplate },
    } = getState() as StoreStateType
    if ((type === 'planning' || type === 'workingSchedule') && !selectedHub) {
      toastFailed('Hub harus diisi')
      return
    }
    if ((type === 'budget' || type === 'workingSchedule') && !dateRangeTemplate) {
      toastFailed('Tanggal harus diisi')
      return
    }
    dispatch(downloadTemplate(type))
  },
)

export const onUploadFile = createAsyncThunk(
  'manPowerBulkUpload/onUploadFile',
  async (
    {
      file,
      type,
    }: { file: File; type: 'workingSchedule' | 'hiringFunnel' | 'planning' | 'jobRequest' },
    { rejectWithValue, getState },
  ) => {
    const {
      auth: { userData },
    } = getState() as StoreStateType

    const formData = new FormData()
    formData.append('file', file)

    if (type === 'workingSchedule') {
      formData.append('file_name', file?.name)
      formData.append('roles', userData?.authorities?.map((role) => role.name)?.join(','))
    }

    if (type === 'hiringFunnel' || type === 'workingSchedule' || type === 'jobRequest') {
      formData.append('updated_by', userData?.email)
    }

    const payload = formData

    try {
      const result = await {
        budget: () => postUploadManPowerBudget(payload),
        planning: () => postUploadManPowerPlanning(payload),
        workingSchedule: () => postUploadWorkingSchedule(payload),
        hiringFunnel: () => postHiringFunnelPrioritizationBulkUpload(payload),
        jobRequest: () => postHiringFunnelJobRequestBulkUpload(payload),
      }[type]()
      return result.data.data
    } catch (error) {
      let errMessage = ''
      if (error instanceof AxiosError) {
        errMessage = error.response?.data?.error?.message || error.message
      }
      toastFailed(errMessage)
      return rejectWithValue(error)
    }
  },
)

export default ManPowerBulkUpload.reducer
